import { useState, type ReactNode } from "react";

import { useAuthConfig } from "../../api/queries";
import { Button } from "../../ui/Button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Field, inputClassName } from "../../ui/Field";
import { Select } from "../../ui/Select";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { Toggle } from "../../ui/Toggle";
import { isValidDomain, userRoleOptions, useWorkspaceSettings } from "./workspace";

/**
 * Who may join, and how. The domain allowlist is exact-match on the part after
 * the `@` (`docs/spec/auth.md` §5 step 10), and emptying it admits anyone the
 * identity provider admits — which the spec requires this screen to confirm
 * explicitly rather than let happen with one stray click.
 */
export function AuthenticationSettingsScreen(): ReactNode {
  const { team, canUpdate, save } = useWorkspaceSettings();
  const config = useAuthConfig();
  const [entry, setEntry] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [confirmingLastRemoval, setConfirmingLastRemoval] = useState<string | null>(null);

  const domains = team?.allowedDomains ?? [];

  const addDomain = (): void => {
    const value = entry.trim().toLowerCase();
    if (value === "") return;
    if (!isValidDomain(value)) {
      setError("Enter a domain such as example.com");
      return;
    }
    if (domains.includes(value)) {
      setError("That domain is already on the list");
      return;
    }
    setError(undefined);
    setEntry("");
    save({ allowedDomains: [...domains, value] });
  };

  const removeDomain = (domain: string): void => {
    if (domains.length === 1) {
      setConfirmingLastRemoval(domain);
      return;
    }
    save({ allowedDomains: domains.filter((item) => item !== domain) });
  };

  return (
    <SettingsPage
      title="Authentication"
      description="How people prove who they are, and who is allowed to join."
    >
      <SettingsSection
        title="Allowed domains"
        description="Exact match on the part after the @. example.com does not admit eu.example.com. An empty list admits anyone the provider admits."
      >
        <ul aria-label="Allowed domains" className="mb-3 flex flex-wrap gap-2">
          {domains.length === 0 ? (
            <li className="text-sm text-muted">
              No restriction — anyone who can sign in may join.
            </li>
          ) : (
            domains.map((domain) => (
              <li
                key={domain}
                className="flex items-center gap-2 rounded-full border border-line px-3 py-1 text-sm"
              >
                <span>{domain}</span>
                {canUpdate && (
                  <button
                    type="button"
                    aria-label={`Remove ${domain}`}
                    className="text-muted hover:text-danger"
                    onClick={() => {
                      removeDomain(domain);
                    }}
                  >
                    ×
                  </button>
                )}
              </li>
            ))
          )}
        </ul>

        {canUpdate && (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label="Add a domain" error={error}>
                {(id) => (
                  <input
                    id={id}
                    value={entry}
                    placeholder="example.com"
                    className={inputClassName}
                    onChange={(event) => {
                      setEntry(event.target.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addDomain();
                      }
                    }}
                  />
                )}
              </Field>
            </div>
            <Button onClick={addDomain}>Add domain</Button>
          </div>
        )}
      </SettingsSection>

      <SettingsSection title="Joining">
        <Toggle
          label="Invitation required"
          description="Checked before the domain allowlist. With it on, nobody joins without being invited."
          checked={team?.inviteRequired ?? false}
          disabled={!canUpdate}
          onChange={(checked) => {
            save({ inviteRequired: checked });
          }}
        />
        <div className="mt-4">
          <Select
            label="Role for new people"
            description="Guests see nothing until something is shared with them."
            value={team?.defaultUserRole ?? "member"}
            options={userRoleOptions}
            disabled={!canUpdate}
            onChange={(value) => {
              save({ defaultUserRole: value });
            }}
          />
        </div>
      </SettingsSection>

      <SettingsSection
        title="Sign-in providers"
        description="Configured where this installation is deployed, not from here."
      >
        {config.isPending ? (
          <p role="status" className="text-sm text-muted">
            Loading providers…
          </p>
        ) : config.isError ? (
          <p role="alert" className="text-sm text-danger">
            {config.error.message}
          </p>
        ) : (
          <ul
            aria-label="Sign-in providers"
            className="divide-y divide-line rounded-md border border-line"
          >
            {(config.data?.providers ?? []).length === 0 ? (
              <li className="px-4 py-3 text-sm text-muted">No providers are configured.</li>
            ) : (
              (config.data?.providers ?? []).map((provider) => (
                <li key={provider.id} className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium">{provider.name}</span>
                  <span className="font-mono text-xs text-muted">{provider.authUrl}</span>
                </li>
              ))
            )}
          </ul>
        )}
      </SettingsSection>

      <ConfirmDialog
        open={confirmingLastRemoval !== null}
        title="Remove the last allowed domain?"
        description="With an empty allowlist, anyone your identity provider admits can join this workspace. Turn on “invitation required” first if that is not what you want."
        confirmLabel="Remove it anyway"
        destructive
        onClose={() => {
          setConfirmingLastRemoval(null);
        }}
        onConfirm={() => {
          setConfirmingLastRemoval(null);
          save({ allowedDomains: [] });
        }}
      />
    </SettingsPage>
  );
}
